import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "../../../css/User/RevolutionDetails.css";

import { FaSpinner } from "react-icons/fa";
import DigitalSignature from "../DigitalSignature/DigitalSignature";
import { useProfile } from "../../../hook/useProfile";
import power from "../../../assets/images/power.png";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Keyboard } from "swiper/modules";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShare } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import ProfileBadge from "../../comman/ProfileBadge";

// ✅ groupMedia uses revolution.images / videos / pdfs directly
const groupMedia = (revolution) => {
  const result = [];
  const images = revolution?.images || [];
  const videos = revolution?.videos || [];
  const pdfs = revolution?.pdfs || [];

  if (images.length > 0)
    result.push({ type: "image", file: images[0], count: images.length });
  if (videos.length > 0)
    result.push({ type: "video", file: videos[0], count: videos.length });
  if (pdfs.length > 0)
    result.push({ type: "pdf", file: pdfs[0], count: pdfs.length });

  return result;
};

// ── inline styles ──────────────────────────────────────────────
const S = {
  mediaRow: {
    display: "flex",
    gap: "10px",
    margin: "14px 0",
  },
  mediaThumb: {
    flex: 1,
    position: "relative",
    borderRadius: "14px",
    overflow: "hidden",
    cursor: "pointer",
    background: "#111",
    aspectRatio: "1 / 1",
    boxShadow: "0 4px 18px rgba(0,0,0,0.18)",
    transition: "transform 0.18s ease, box-shadow 0.18s ease",
  },
  mediaImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  mediaVideo: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    pointerEvents: "none",
  },
  pdfThumb: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #c0392b 60%, #e74c3c 100%)",
    color: "#fff",
  },
  pdfIcon: {
    fontSize: "32px",
    marginBottom: "4px",
    lineHeight: 1,
  },
  pdfLabel: {
    fontSize: "13px",
    fontWeight: "700",
    letterSpacing: "1px",
  },
  overlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.08) 55%)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    padding: "10px 12px",
  },
  badgeRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  typeTag: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#fff",
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    opacity: 0.85,
  },
  countBadge: {
    background: "rgba(255,160,0,0.92)",
    color: "#fff",
    fontSize: "12px",
    fontWeight: "800",
    borderRadius: "20px",
    padding: "2px 10px",
    backdropFilter: "blur(4px)",
    boxShadow: "0 2px 8px rgba(255,140,0,0.35)",
  },
  playIcon: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.22)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    color: "#fff",
    border: "2px solid rgba(255,255,255,0.45)",
  },
};

const RevolutionDetailsCard = ({
  revolution = {},
  currentUserId,
  supportStatus = {},
  commentInput,
  setCommentInput,
  submitting,
  signatureSubmitted,
  handleSupport,
  handleIgnore,
  submitSupport,
  setSignatureData,
  handlers,
}) => {
  const { isProfileInfo } = useProfile();

  const isOwner = revolution?.from?._id === currentUserId;
  const profileLink = isOwner
    ? `/my/${revolution?.from?._id}/profile`
    : `/user/${revolution?.from?._id}/profile`;

  const isBatchPost = revolution?.from?.isBatch;

  const [activeMedia, setActiveMedia] = useState([]);
  const [initialSlide, setInitialSlide] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const openMedia = (mediaArray, index) => {
    setActiveMedia(mediaArray || []);
    setInitialSlide(index || 0);
    setShowModal(true);
  };

  // ✅ Video auto pause on scroll
  useEffect(() => {
    const videos = document.querySelectorAll("video");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) entry.target.pause();
        });
      },
      { threshold: 0.6 }
    );
    videos.forEach((video) => observer.observe(video));
    return () => videos.forEach((video) => observer.unobserve(video));
  }, [showModal, activeMedia]);

  // ✅ separatedMedia reads revolution.images / videos / pdfs directly
  const separatedMedia = React.useMemo(() => {
    return {
      images: (revolution?.images || []).map((url) => ({ type: "image", url })),
      videos: (revolution?.videos || []).map((url) => ({ type: "video", url })),
      pdfs: (revolution?.pdfs || []).map((url) => ({ type: "pdf", url })),
    };
  }, [revolution]);

  const handleAadhaarNotVerified = () => {
    switch (isProfileInfo?.adhaarVerified) {
      case "NONE":
        toast.info("Please verify your Aadhaar.");
        break;
      case "REQUESTED":
        toast.warning("Verification under review.");
        break;
      case "REJECTED":
        toast.error("Verification rejected.");
        break;
      default:
        toast.info("Verification required.");
    }
  };

  const grouped = groupMedia(revolution);

  return (
    <div className="revolution-details-card">
      <h4 className="revolution-title text-warning text-center">
        Online Revolution organized by
      </h4>

      {/* ORGANIZER */}
      <div className="d-flex flex-row justify-content-between">
        <div className="organizer-info">
          <ProfileBadge
            isBatch={isBatchPost}
            ProfileLink={profileLink}
            profilePic={revolution?.from?.profilePic}
          />
          <div className="organizer-details">
            <div className="organizer-name">
              <Link
                to={profileLink}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <h3>{revolution?.from?.fullName || "Unknown"}</h3>
              </Link>
              {revolution?.from?.adhaarVerified === "VERIFIED" && (
                <span className="feed-verified-badge">
                  <Icon icon="ic:baseline-verified" />
                </span>
              )}
            </div>
            <p style={{ fontSize: "12px", color: "#555", margin: "-3px" }}>
              {revolution?.createdAt
                ? new Date(revolution.createdAt).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  }) +
                  ", " +
                  new Date(revolution.createdAt)
                    .toLocaleTimeString("en-GB", {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    })
                    .replace("AM", "am")
                    .replace("PM", "pm")
                : ""}
            </p>
          </div>
        </div>
        <img src={power} alt="" className="revolution-icon" />
      </div>

      {/* TO & SUBJECT */}
      {revolution?.to && (
        <p style={{ margin: "12px 0 6px 0", fontSize: "14px" }}>
          <span style={{ color: "#aaa", fontWeight: "500" }}>To: &nbsp;</span>
          <span style={{ color: "#555", fontWeight: "600", fontSize: "15px" }}>
            {revolution.to}
          </span>
        </p>
      )}

      {(revolution?.subject ||
        revolution?.Subject ||
        revolution?.title ||
        revolution?.Topic) && (
        <p style={{ margin: "6px 0 12px 0", fontSize: "14px" }}>
          <span style={{ color: "#aaa", fontWeight: "500" }}>
            Subject: &nbsp;
          </span>
          <span style={{ color: "#555", fontWeight: "600", fontSize: "15px" }}>
            {revolution?.subject ||
              revolution?.Subject ||
              revolution?.title ||
              revolution?.Topic}
          </span>
        </p>
      )}

      {/* DESCRIPTION */}
      <p>{revolution?.description || "No description"}</p>

      {/* ✅ MEDIA SECTION */}
      {grouped.length > 0 && (
        <div style={S.mediaRow}>
          {grouped.map((media, index) => (
            <div
              key={index}
              style={{
                ...S.mediaThumb,
                transform:
                  hoveredIndex === index ? "scale(1.03)" : "scale(1)",
                boxShadow:
                  hoveredIndex === index
                    ? "0 8px 28px rgba(0,0,0,0.28)"
                    : "0 4px 18px rgba(0,0,0,0.18)",
              }}
              onClick={() =>
                openMedia(
                  separatedMedia[
                    media.type === "image"
                      ? "images"
                      : media.type === "video"
                      ? "videos"
                      : "pdfs"
                  ],
                  0
                )
              }
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* IMAGE */}
              {media.type === "image" && (
                <>
                  <img src={media.file} alt="media" style={S.mediaImg} />
                  <div style={S.overlay}>
                    <div style={S.badgeRow}>
                      <span style={S.typeTag}>Images</span>
                      <span style={S.countBadge}>{media.count}</span>
                    </div>
                  </div>
                </>
              )}

              {/* VIDEO */}
              {media.type === "video" && (
                <>
                  <video src={media.file} muted loop style={S.mediaVideo} />
                  <div style={S.playIcon}>▶</div>
                  <div style={S.overlay}>
                    <div style={S.badgeRow}>
                      <span style={S.typeTag}>Videos</span>
                      <span style={S.countBadge}>{media.count}</span>
                    </div>
                  </div>
                </>
              )}

              {/* PDF */}
              {media.type === "pdf" && (
                <>
                  <div style={S.pdfThumb}>
                    <div style={S.pdfIcon}>📄</div>
                    <div style={S.pdfLabel}>PDF</div>
                  </div>
                  <div style={S.overlay}>
                    <div style={S.badgeRow}>
                      <span style={S.typeTag}>PDFs</span>
                      <span style={S.countBadge}>{media.count}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* SUPPORT ACTIONS */}
      <div className="support-section">
        <div className="support-buttons">
          <button
            className={`revolution-support-btn btn-support rounded-pill ${
              supportStatus[revolution._id] ? "active" : ""
            }`}
            onClick={() => {
              if (isProfileInfo?.adhaarVerified === "VERIFIED") {
                handleSupport(revolution._id);
              } else {
                handleAadhaarNotVerified();
              }
            }}
            disabled={isOwner}
            title={isOwner ? "You cannot support your own revolution" : ""}
          >
            {revolution.userAction === "SUPPORTED" ? "Supported" : "Support"}
          </button>

          {true && (
            <button
              className="btn-ignore rounded-pill"
              onClick={() => handleIgnore(revolution._id)}
              disabled={isOwner}
              title={isOwner ? "You cannot ignore your own revolution" : ""}
            >
              Ignore
            </button>
          )}
        </div>
      </div>

      {/* SIGNATURE */}
      {supportStatus?.[revolution?._id] === "support" &&
        revolution?.userAction === "NONE" && (
          <div className="ds-section">
            <DigitalSignature
              onChange={(sig) => setSignatureData?.(sig)}
              isSubmitted={signatureSubmitted}
            />
            <textarea
              className="ds-comment-box"
              rows={4}
              placeholder="Write your support comment here"
              value={commentInput || ""}
              onChange={(e) => setCommentInput?.(e.target.value)}
            />
            <button
              className="ds-submit-btn"
              onClick={() => submitSupport?.("support", revolution?._id)}
              disabled={submitting}
            >
              {submitting ? <FaSpinner /> : "Submit"}
            </button>
          </div>
        )}

      {/* ✅ MODAL */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.96)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Close button */}
          <button
            onClick={() => setShowModal(false)}
            style={{
              position: "absolute",
              top: 18,
              right: 22,
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.12)",
              border: "1.5px solid rgba(255,255,255,0.25)",
              color: "#fff",
              fontSize: "20px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              backdropFilter: "blur(6px)",
            }}
          >
            ✕
          </button>

          <div style={{ width: "100%", height: "100%" }}>
            <Swiper
              modules={[Navigation, Pagination, Keyboard]}
              initialSlide={initialSlide}
              navigation
              pagination={{ clickable: true }}
              keyboard={{ enabled: true }}
              style={{ height: "100%" }}
              onSlideChange={() => {
                document.querySelectorAll("video").forEach((v) => v.pause());
              }}
            >
              {activeMedia?.map((item, index) => (
                <SwiperSlide key={index}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      padding: "48px 16px 32px",
                    }}
                  >
                    {/* IMAGE */}
                    {item.type === "image" && (
                      <TransformWrapper>
                        <TransformComponent>
                          <img
                            src={item.url}
                            alt=""
                            style={{
                              maxWidth: "100%",
                              maxHeight: "88vh",
                              objectFit: "contain",
                              borderRadius: "10px",
                            }}
                          />
                        </TransformComponent>
                      </TransformWrapper>
                    )}

                    {/* VIDEO */}
                    {item.type === "video" && (
                      <video
                        src={item.url}
                        controls
                        autoPlay
                        style={{
                          maxWidth: "100%",
                          maxHeight: "88vh",
                          borderRadius: "10px",
                        }}
                      />
                    )}

                    {/* ✅ PDF — Google Docs Viewer (no download, preview in browser) */}
                    {item.type === "pdf" && (
                      <div
                        style={{
                          width: "100%",
                          height: "88vh",
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                        >
                        {/* Download button — only if user wants it
                        <button
                          onClick={() => handleDownloadPDF(item.url)}
                          style={{
                            background: "rgba(255,160,0,0.92)",
                            color: "#fff",
                            padding: "7px 18px",
                            borderRadius: "20px",
                            fontWeight: "700",
                            fontSize: "13px",
                            border: "none",
                            cursor: "pointer",
                          }}
                        >
                          ⬇ Download PDF
                        </button> */}

                        {/* Google Docs Viewer — renders PDF inline */}
                        <iframe
                          src={`https://docs.google.com/gview?url=${encodeURIComponent(
                            item.url
                          )}&embedded=true`}
                          title="PDF Preview"
                          style={{
                            width: "100%",
                            flex: 1,
                            background: "#fff",
                            borderRadius: "10px",
                            border: "none",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      )}

      {/* SUPPORTER COUNT */}
      <div className="d-flex justify-content-between align-items-center border-top mt-4">
        <span>
          <span className="theme-color">
            {revolution?.supporterCount ?? 0}
          </span>{" "}
          <span className="text-secondary">Supporter</span>
        </span>
        <FontAwesomeIcon
          icon={faShare}
          onClick={() => handlers?.onShare?.(revolution)}
        />
      </div>
    </div>
  );
};

export default RevolutionDetailsCard;